import React, { useEffect, useState } from "react";
import api, { errMsg, avatarSrc, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/PageHeader";
import { ImageUpload } from "@/components/ImageUpload";
import { StarRating } from "@/components/StarRating";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, Plane, Package, XCircle, Mail, Phone, Pencil } from "lucide-react";

const VERIF = {
  verified: { icon: ShieldCheck, color: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Verified" },
  pending: { icon: ShieldQuestion, color: "text-amber-600 bg-amber-50 border-amber-200", label: "Pending review" },
  rejected: { icon: ShieldAlert, color: "text-rose-600 bg-rose-50 border-rose-200", label: "Rejected" },
  unverified: { icon: ShieldAlert, color: "text-slate-600 bg-slate-50 border-slate-200", label: "Not verified" },
};

export default function Profile() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", bio: "", picture_file_id: null });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [govId, setGovId] = useState(null);
  const [reviews, setReviews] = useState([]);

  const load = async () => {
    const { data } = await api.get("/profile/me");
    setData(data);
    setForm({ name: data.user.name || "", phone: data.user.phone || "", bio: data.user.bio || "", picture_file_id: data.user.picture_file_id || null });
    setGovId(data.user.gov_id_file_id || null);
    try {
      const rv = await api.get(`/users/${data.user.id}/reviews`);
      setReviews(rv.data.reviews);
    } catch { /* reviews are non-fatal */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", form); toast.success("Profile updated."); await load(); refreshUser(); setEditing(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const submitVerify = async () => {
    if (!govId) { toast.error("Please upload your government ID first."); return; }
    setVerifying(true);
    try { await api.post("/profile/verify", { gov_id_file_id: govId }); toast.success("Verification submitted for review."); await load(); refreshUser(); }
    catch (e) { toast.error(errMsg(e)); } finally { setVerifying(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;
  const v = VERIF[data.user.verification_status] || VERIF.unverified;
  const VIcon = v.icon;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Profile" subtitle="Manage your public identity and verification." />

      <div className="rounded-2xl bg-primary p-6 text-white flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-bold overflow-hidden">{avatarSrc(data.user) ? <img src={avatarSrc(data.user)} alt={data.user.name} className="w-full h-full object-cover" /> : data.user.name?.[0]?.toUpperCase()}</div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold truncate">{data.user.name}</h2>
          {data.user.rating_count > 0 && (
            <div className="flex items-center gap-1.5 mt-0.5" data-testid="profile-rating">
              <StarRating value={data.user.rating_avg} />
              <span className="text-white/90 text-sm font-medium">{data.user.rating_avg} · {data.user.rating_count} review{data.user.rating_count !== 1 ? "s" : ""}</span>
            </div>
          )}
          <p className="text-white/80 text-sm flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {data.user.email}</p>
          {data.user.phone && <p className="text-white/80 text-sm flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {data.user.phone}</p>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center" data-testid="stat-trips"><Plane className="w-5 h-5 text-primary mx-auto" /><p className="text-2xl font-bold mt-1">{data.stats.trips}</p><p className="text-xs text-slate-400">Trips</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center" data-testid="stat-deliveries"><Package className="w-5 h-5 text-primary mx-auto" /><p className="text-2xl font-bold mt-1">{data.stats.deliveries}</p><p className="text-xs text-slate-400">Deliveries</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center" data-testid="stat-cancellations"><XCircle className="w-5 h-5 text-rose-400 mx-auto" /><p className="text-2xl font-bold mt-1">{data.stats.cancellations}</p><p className="text-xs text-slate-400">Cancellations</p></div>
      </div>

      <SectionCard title="Verification" className="mb-6">
        <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${v.color}`} data-testid="verification-status">
          <span className="flex items-center gap-2 font-medium"><VIcon className="w-5 h-5" /> {v.label}</span>
        </div>

        {["unverified", "rejected"].includes(data.user.verification_status) ? (
          <div className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <Label>Government ID document <span className="text-rose-500">*</span></Label>
              <ImageUpload value={govId} onChange={setGovId} purpose="gov_id" testid="gov-id-upload"
                hint="Upload a clear photo of a government-issued ID (passport, driver's licence, Aadhaar, etc.). JPG/PNG/WEBP, max 8 MB." />
            </div>
            <Button onClick={submitVerify} disabled={verifying || !govId} data-testid="submit-verification-btn" className="rounded-xl">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for verification"}
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="gov-id-on-file">
            <Label>Government ID on file</Label>
            {govId ? (
              <img src={fileUrl(govId)} alt="Government ID" className="w-full max-h-56 object-contain rounded-xl border border-slate-200 bg-slate-50" data-testid="gov-id-preview" />
            ) : <p className="text-sm text-slate-400">No document on file.</p>}
          </div>
        )}

        <p className="text-xs text-slate-400 mt-3">Your government ID is private and encrypted at rest — it is only visible to you and Pack Mate's verification team. It is never shown to other users.</p>
      </SectionCard>

      <SectionCard title="Personal details" action={!editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="profile-edit-btn" className="rounded-lg"><Pencil className="w-3.5 h-3.5" /> Edit</Button>}>
        {!editing ? (
          <div className="space-y-3" data-testid="profile-view">
            <div><p className="text-xs text-slate-400">Full name</p><p className="text-sm font-medium text-slate-800">{data.user.name || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Phone number</p><p className="text-sm font-medium text-slate-800">{data.user.phone || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Bio</p><p className="text-sm font-medium text-slate-800 whitespace-pre-wrap">{data.user.bio || "—"}</p></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Profile photo</Label><ImageUpload value={form.picture_file_id} onChange={(picture_file_id) => setForm((f) => ({ ...f, picture_file_id }))} purpose="profile" testid="profile-photo" round /></div>
            <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Phone number</Label><Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Bio</Label><Textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} className="rounded-xl" rows={3} placeholder="Tell others a little about you" /></div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="rounded-xl">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}</Button>
              <Button variant="outline" onClick={() => { setForm({ name: data.user.name || "", phone: data.user.phone || "", bio: data.user.bio || "", picture_file_id: data.user.picture_file_id || null }); setEditing(false); }} data-testid="profile-cancel-btn" className="rounded-xl">Cancel</Button>
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2">Reviews {data.user.rating_count > 0 && <span className="text-sm font-normal text-slate-400">· {data.user.rating_avg} avg</span>}</span>} className="mb-6">
        {reviews.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="reviews-empty">No reviews yet. Completed deliveries will appear here.</p>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-primary font-semibold text-sm overflow-hidden flex-shrink-0">
                      {avatarSrc(r.reviewer) ? <img src={avatarSrc(r.reviewer)} alt="" className="w-full h-full object-cover" /> : r.reviewer_name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.reviewer_name}</p>
                      <p className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <StarRating value={r.rating} />
                </div>
                {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
